import { useMemo, useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { useApi } from "@/hooks/useApi";
import StageTracker from "@/components/base/StageTracker";
import { useNotifications } from "@/context/NotificationContext";
import api from "@/services/api";
import { convertDriveLink } from "@/utils/convertDriveLink";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import ProjectModal, { ProjectFormState } from "./components/ProjectModal";

type Stage = "kickoff" | "design" | "development" | "testing" | "launch";
type Status = "active" | "paused" | "completed" | "cancelled";

interface Project {
  id: string;
  title: string;
  description: string | null;
  status: Status;
  stage: Stage;
  progress: number;
  client_id: string;
  budget: string | null;
  spent: string | null;
  start_date: string | null;
  due_date: string | null;
  tech_stack: string[];
  team: string[];
  screenshots?: string[];
  thumbnail_url?: string;
  video_url?: string;
  video_type?: string;
  featured?: boolean;
  add_to_portfolio?: boolean;
}

interface ProjectUpdate {
  id: string;
  author_name: string;
  author_role: string;
  author_job_title: string | null;
  message: string;
  created_at: string;
}

export default function AdminProjects() {
  const formatFcfa = (value: string | null | undefined) => {
    if (value === null || value === undefined) return "N/A";
    const raw = String(value).trim();
    if (!raw) return "N/A";

    // Keep only numeric characters, '.' and ',' for safe parsing.
    const normalized = raw.replace(/[^0-9.,-]/g, "");
    const num = Number(normalized.replace(/,/g, ""));
    if (!Number.isFinite(num)) return raw;

    // Group with commas for readability; show as FCFA.
    return `FCFA ${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  };
  const { showToast } = useNotifications();
  const [selected, setSelected] = useState<Project | null>(null);

  const { data: projects, loading, refetch } = useApi<Project[]>("/projects");

  const { data: clients } = useApi<{ id: string; company_name: string }[]>("/clients");
  const { data: teamMembers } = useApi<{ id: string; full_name: string }[]>("/users/team");

  const [newUpdate, setNewUpdate] = useState("");
  const [updateKey, setUpdateKey] = useState(0);

  const { data: projectDetail } = useApi<{ updates: ProjectUpdate[] }>(
    selected ? `/projects/${selected.id}?key=${updateKey}` : "",
    { enabled: !!selected }
  );

  const clientMap = useMemo(() => {
    const map: Record<string, string> = {};
    (clients || []).forEach(c => map[c.id] = c.company_name);
    return map;
  }, [clients]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<(Partial<ProjectFormState> & { collaborators?: string }) | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);

  const handleCreateProject = async (payload: {
    client_id: string;
    title: string;
    description?: string;
    stage: string;
    status: string;
    progress: number;
    budget?: string;
    spent?: string;
    start_date?: string;
    due_date?: string;
    tech_stack?: string[];
    team: string[];
    add_to_portfolio?: boolean;
  }) => {
    await api.post("/projects", payload);
    showToast("success", "Project created.");
    await refetch();
    setCreateOpen(false);
  };

  const handleEditProject = async (payload: {
    client_id: string;
    title: string;
    description?: string;
    stage: string;
    status: string;
    progress: number;
    budget?: string;
    spent?: string;
    start_date?: string;
    due_date?: string;
    tech_stack?: string[];
    team: string[];
    collaborators?: string[];
    screenshots?: string[];
    thumbnail_url?: string;
    video_url?: string;
    video_type?: string;
    featured?: boolean;
    add_to_portfolio?: boolean;
  }) => {
    if (!editingId) return;
    await api.patch(`/projects/${editingId}`, payload);
    showToast("success", "Project updated.");
    await refetch();
    setEditOpen(false);
    setEditingProject(null);
    setEditingId(null);
  };

  const handleAddUpdate = async () => {
    if (!selected || !newUpdate.trim()) return;
    try {
      await api.post(`/projects/${selected.id}/updates`, { message: newUpdate.trim() });
      showToast("success", "Update added.");
      setNewUpdate("");
      setUpdateKey(prev => prev + 1); // Force refetch of project detail
    } catch (err: any) {
      showToast("error", err?.message || "Failed to add update.");
    }
  };

  const openEditModal = (project: Project) => {
    const formData: Partial<ProjectFormState> & { collaborators?: string } = {

      client_id: project.client_id,
      title: project.title,
      description: project.description || "",
      stage: project.stage,
      status: project.status,
      progress: project.progress,
      budget: project.budget || "",
      spent: project.spent || "",
      start_date: project.start_date || "",
      due_date: project.due_date || "",
      tech_stack: Array.isArray(project.tech_stack) ? project.tech_stack.join(", ") : (project.tech_stack as unknown as string) || "",
      team: Array.isArray(project.team) ? project.team : [],
      collaborators:
        typeof (project as any).collaborators === "string"
          ? (project as any).collaborators
          : Array.isArray((project as any).collaborators)
            ? (project as any).collaborators.join(", ")
            : "",
      screenshots: Array.isArray(project.screenshots) ? project.screenshots : [],
      thumbnail_url: project.thumbnail_url || "",
      video_url: project.video_url || "",
      video_type: project.video_type || "youtube",
      featured: project.featured || false,
      add_to_portfolio: (project as any).add_to_portfolio || false,
    };
    setEditingProject(formData);
    setEditingId(project.id);
    setEditOpen(true);
  };


  const statusColors: Record<string, string> = {
    active: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
    paused: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    completed: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    cancelled: "text-cyber-red border-cyber-red/30 bg-cyber-red/10",
  };

  const stageLabels: Record<string, string> = {
    kickoff: "Kickoff",
    design: "Design",
    development: "Development",
    testing: "Testing",
    launch: "Launch",
  };

  const stageIndex = (stage: string) => {
    const stages = ["kickoff", "design", "development", "testing", "launch"];
    return stages.indexOf(stage);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Projects</h1>
            <p className="text-silver text-sm mt-1">{projects?.length || 0} total projects</p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-semibold px-4 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" /> New Project
          </button>

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-3">
            {projects?.map((project) => (
              <div key={project.id} onClick={() => setSelected(project)}
                className={`bg-dark-card border rounded-xl p-4 cursor-pointer transition-all ${selected?.id === project.id ? "border-electric-blue/40 bg-electric-blue/5" : "border-dark-border hover:border-electric-blue/20"}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-white text-sm font-semibold leading-snug">{project.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${statusColors[project.status]}`}>{project.status}</span>
                </div>
                  <p className="text-silver-dim text-xs mb-3">{clientMap[project.client_id] || project.client_id}</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-dark-border rounded-full h-1.5">
                    <div className="bg-electric-blue h-1.5 rounded-full" style={{ width: `${project.progress}%` }} />
                  </div>
                  <span className="text-electric-blue text-xs font-bold">{project.progress}%</span>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            {selected ? (
              <div className="bg-dark-card border border-dark-border rounded-2xl p-6 space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{selected.title}</h2>
                    <p className="text-silver-dim text-sm mt-1">{clientMap[selected.client_id] || selected.client_id}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(selected)}
                      className="flex items-center gap-1.5 border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 px-3 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line" /> Edit
                    </button>

                    <button
                      onClick={() => {
                        // Soft delete supported by backend (projects.deleted_at)
                        // Use confirmation to avoid accidental deletions.
                        const ok = window.confirm(`Delete project "${selected.title}"?`);
                        if (!ok) return;

                        api
                          .delete(`/projects/${selected.id}`)
                          .then(async () => {
                            showToast("success", "Project deleted.");
                            setSelected(null);
                            await refetch();
                          })
                          .catch((err: any) => {
                            showToast("error", err?.message || "Failed to delete project.");
                          });
                      }}
                      className="flex items-center gap-1.5 border border-dark-border text-silver hover:text-white hover:border-cyber-red/40 px-3 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap"
                      aria-label="Delete"
                      title="Delete project"
                    >
                      <i className="ri-delete-bin-line" /> Delete
                    </button>
                  </div>
                </div>

                <p className="text-silver text-sm leading-relaxed">{selected.description || "No description."}</p>

                {Array.isArray(selected.screenshots) && selected.screenshots.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-3">Screenshots</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selected.screenshots.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block">
                          <img
                            src={convertDriveLink(url)}
                            alt={`Screenshot ${idx + 1}`}
                            className="w-full aspect-video object-cover rounded-xl border border-dark-border hover:border-electric-blue/50 transition-colors"
                            referrerPolicy="no-referrer"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-white font-semibold text-sm mb-4">Project Stage</h3>
                  <StageTracker currentStage={selected.stage} currentIndex={stageIndex(selected.stage)} />
                </div>



                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Stage", value: stageLabels[selected.stage] || selected.stage },
                    { label: "Progress", value: `${selected.progress}%` },
                    {
                      label: "Start Date",
                      value: selected.start_date
                        ? new Date(selected.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : "N/A",
                    },
                    {
                      label: "Due Date",
                      value: selected.due_date
                        ? new Date(selected.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : "N/A",
                    },
                    { label: "Budget", value: formatFcfa(selected.budget) },
                    { label: "Spent", value: formatFcfa(selected.spent) },
                  ].map((m) => (
                    <div key={m.label} className="bg-dark-panel border border-dark-border rounded-xl p-3">
                      <span className="text-silver-dim text-xs uppercase tracking-wider">{m.label}</span>
                      <p className="text-white text-sm font-semibold mt-0.5">{m.value}</p>
                    </div>
                  ))}
                </div>


                <div>
                  <h3 className="text-white font-semibold text-sm mb-3">Team</h3>
                  <div className="flex flex-wrap gap-2">
                    {(selected.team || []).map((member: string) => (
                      <div key={member} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-panel border border-dark-border">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-electric-blue/20 text-electric-blue text-xs font-bold">
                          {member.split(" ").map((n) => n[0]).join("")}
                        </div>
                        <span className="text-silver text-xs">{member}</span>
                      </div>
                    ))}
                  </div>

                  <h3 className="text-white font-semibold text-sm mb-3 mt-4">Collaborators</h3>
                  <div className="flex flex-wrap gap-2">
                    {typeof (selected as any).collaborators === "string" && (selected as any).collaborators.trim().length > 0
                      ? (selected as any).collaborators
                          .split(",")
                          .map((x: string) => x.trim())
                          .filter(Boolean)
                          .map((collab: string) => (
                            <div key={collab} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-panel border border-dark-border">
                              <span className="text-silver text-xs">{collab}</span>
                            </div>
                          ))
                      : Array.isArray((selected as any).collaborators)
                        ? (selected as any).collaborators.map((collab: string) => (
                            <div key={collab} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-panel border border-dark-border">
                              <span className="text-silver text-xs">{collab}</span>
                            </div>
                          ))
                        : (
                            <p className="text-silver-dim text-xs">No collaborators</p>
                          )}
                  </div>
                </div>


                <div>
                  <h3 className="text-white font-semibold text-sm mb-3">Tech Stack</h3>
                  <div className="flex flex-wrap gap-2">
                    {(selected.tech_stack || []).map((t: string) => (
                      <span key={t} className="text-xs px-2.5 py-1 rounded-lg bg-deep-black border border-dark-border text-silver">{t}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-white font-semibold text-sm mb-3">Recent Updates</h3>
                  <div className="space-y-3">
                    {(projectDetail?.updates || []).map((upd) => (
                      <div key={upd.id} className="flex gap-3 p-3 rounded-xl bg-dark-panel border border-dark-border">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-electric-blue/20 text-electric-blue text-xs font-bold flex-shrink-0">
                          {upd.author_name.split(" ").map((n) => n[0]).join("")}
                        </div>
                        <div>
                           <div className="flex items-center gap-2 mb-1">
                             <span className="text-white text-xs font-semibold">{upd.author_name}</span>
                             <span className="text-silver-dim text-xs">{upd.author_role}</span>
                             {upd.author_job_title && <span className="text-electric-blue text-xs">({upd.author_job_title})</span>}
                           </div>
                          <p className="text-silver text-xs leading-relaxed">{upd.message}</p>
                        </div>
                      </div>
                    ))}
                    {(!projectDetail?.updates || projectDetail.updates.length === 0) && (
                      <p className="text-silver-dim text-sm">No updates yet.</p>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block">
                      <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Add Update</span>
                      <div className="flex gap-2 mt-2">
                        <textarea
                          value={newUpdate}
                          onChange={(e) => setNewUpdate(e.target.value)}
                          className="flex-1 bg-dark-card border border-dark-border rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
                          rows={2}
                          placeholder="Write an update..."
                        />
                        <button
                          onClick={handleAddUpdate}
                          disabled={!newUpdate.trim()}
                          className="px-4 py-2 bg-electric-blue text-deep-black font-medium rounded-xl hover:shadow-glow-blue transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-dark-card border border-dark-border rounded-2xl flex items-center justify-center h-64">
                <div className="text-center">
                  <i className="ri-folder-line text-4xl text-silver-dim mb-3 block" />
                  <p className="text-silver text-sm">Select a project to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <ProjectModal
          open={createOpen}
          mode="create"
          clients={(clients || []).map((c) => ({ id: c.id, company_name: c.company_name }))}
          teamMembers={(teamMembers || []).map((t) => ({ id: t.id, full_name: t.full_name }))}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (payload) => {
            await handleCreateProject(payload);
          }}
        />
        <ProjectModal
          open={editOpen}
          mode="edit"
          clients={(clients || []).map((c) => ({ id: c.id, company_name: c.company_name }))}
          teamMembers={(teamMembers || []).map((t) => ({ id: t.id, full_name: t.full_name }))}
          initial={editingProject}
          onClose={() => {
            setEditOpen(false);
            setEditingProject(null);
            setEditingId(null);
          }}
          onSubmit={async (payload) => {
            await handleEditProject(payload);
          }}
        />
      </div>
    </AdminLayout>
  );
}